import { KeyValuePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  OnInit,
} from '@angular/core';

import { type } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { HeaderComponent } from 'app/layouts/header/header.component';
import { createLoader } from 'flatgeobuf/lib/mjs/ol.js';

import { Map, Overlay, View } from 'ol';
import { FeatureLike } from 'ol/Feature';
import { FeatureLoader } from 'ol/featureloader';
import MVT from 'ol/format/MVT';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorTileLayer from 'ol/layer/VectorTile';
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import WebGLVectorTileLayer from 'ol/layer/WebGLVectorTile';
import { OSM, Vector } from 'ol/source';
import VectorTileSource from 'ol/source/VectorTile';

import { GetValueFromIdPipe } from '@shared/pipes/filter.pipe';

import { RtudataComponent } from './rtudata.component';
import { RtudataStore } from './rtudata.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    KeyValuePipe,
    HeaderComponent,
    RtudataComponent,
    GetValueFromIdPipe,
    TranslatePipe,
  ],
  selector: 'norupe-rtudata-map',
  templateUrl: './rtudata-map.component.html',
})
export class RtuDataMapComponent implements OnInit {
  #cdr = inject(ChangeDetectorRef);
  rtuDataStore = inject(RtudataStore);
  #entrypoint = window.location.origin.includes('localhost')
    ? 'http://localhost:7800/'
    : `${window.location.origin}/tiles/`;

  map = new Map();

  featuresOnCursor: FeatureLike[] = [];

  objectStyle = [
    {
      filter: ['==', ['get', 'status'], 1],
      style: {
        'circle-fill-color': 'rgba(163, 141, 40,0.3)',
        'circle-radius': 4,
        'circle-stroke-color': 'rgba(163, 141, 40,1)',
        'circle-stroke-width': 1,
        'fill-color': 'rgba(163, 141, 40,0.3)',
        'stroke-color': 'rgba(163, 141, 40,1)',
        'stroke-width': 1,
      },
    },
    {
      filter: ['==', ['get', 'status'], 2],
      style: {
        'circle-fill-color': 'rgba(19, 126, 42,0.3)',
        'circle-radius': 4,
        'circle-stroke-color': 'rgba(19, 126, 42,1)',
        'circle-stroke-width': 1,
        'fill-color': 'rgba(19, 126, 42,0.3)',
        'stroke-color': 'rgba(19, 126, 42,1)',
        'stroke-width': 1,
      },
    },
    {
      filter: ['==', ['get', 'status'], 0],
      style: {
        'circle-fill-color': 'rgba(124, 117, 117,0.3)',
        'circle-radius': 4,
        'circle-stroke-color': 'rgba(124, 117, 117,1)',
        'circle-stroke-width': 1,
        'fill-color': 'rgba(124, 117, 117,0.3)',
        'stroke-color': 'rgba(124, 117, 117,1)',
        'stroke-width': 1,
      },
    },
    {
      filter: ['==', ['get', 'status'], 3],
      style: {
        'circle-fill-color': 'rgba(184, 53, 53,0.3)',
        'circle-radius': 4,
        'circle-stroke-color': 'rgba(184, 53, 53,1)',
        'circle-stroke-width': 1,
        'fill-color': 'rgba(184, 53, 53,0.3)',
        'stroke-color': 'rgba(184, 53, 53,1)',
        'stroke-width': 1,
      },
    },
  ];

  constructor() {
    effect(() => {
      this.map.getAllLayers().map((layer) => {
        if (
          layer.getProperties()['name'] === this.rtuDataStore.activeLayer() ||
          layer.getProperties()['name'] === 'OSM'
        ) {
          layer.setVisible(true);
        } else {
          layer.setVisible(false);
        }
      });
    });
    effect(() => {
      this.map.getAllLayers().map((layer) => {
        if (layer.getProperties()['name'] === 'ndvi') {
          const ndviSource = new Vector({});

          const loader: FeatureLoader = createLoader(
            ndviSource,
            this.rtuDataStore.selectedScan()?.ndvi_fgb_file!,
          );
          ndviSource.setLoader(loader);
          ndviSource.set('name', type);
          layer.setSource(ndviSource);
        } else if (layer.getProperties()['name'] === 'thermal') {
          const thermalSource = new Vector({});

          const loader: FeatureLoader = createLoader(
            thermalSource,
            this.rtuDataStore.selectedScan()?.thermal_fgb_file!,
          );
          thermalSource.setLoader(loader);
          thermalSource.set('name', type);
          layer.setSource(thermalSource);
        } else if (layer.getProperties()['name'] === 'objects') {
          const objectSource = new VectorTileSource({
            format: new MVT(),
            url: `${this.#entrypoint}public.plant_objects_mvt/{z}/{x}/{y}.pbf?session_id=${this.rtuDataStore.selectedScan()?.id}`,
          }) as any;
          layer.setSource(objectSource);
        }
      });
    });
  }

  ngOnInit(): void {
    const thermalSource = new Vector({});

    const ndviSource = new Vector({});

    const objectSource = new VectorTileSource({
      format: new MVT(),
    });

    this.map = new Map({
      layers: [
        this.#createWebGLObjectLayer(objectSource),
        this.#createObjectLayer(objectSource),
        this.#createThermalLayer(thermalSource),
        this.#createThermalWebGLLayer(thermalSource),
        this.#createNdviLayer(ndviSource),
        this.#createNdviWebGLLayer(ndviSource),
        new TileLayer({
          properties: { name: 'OSM' },
          source: new OSM(),
        }),
      ],
      target: 'map',
      view: new View({
        center: [2743253, 7726360],
        minZoom: 17,
        zoom: 20,
      }),
    });

    const overlay = this.#createOverlay();

    this.map.addOverlay(overlay);

    this.map.on('loadstart', () => {
      this.rtuDataStore.setLoadingMap(true);
    });

    this.map.on('loadend', () => {
      this.rtuDataStore.setLoadingMap(false);
    });

    this.map.on('pointermove', (event) => {
      this.featuresOnCursor = this.map.getFeaturesAtPixel(event.pixel);
      this.map.getTargetElement().style.cursor =
        this.featuresOnCursor.length > 0 ? 'pointer' : '';
      if (this.featuresOnCursor.length > 0) {
        overlay.setPosition(event.coordinate);
      } else {
        overlay.setPosition(undefined);
      }
      this.#cdr.detectChanges();
    });
  }

  #createOverlay() {
    const overlayEl = document.getElementById(
      'overlay-popup',
    ) as HTMLImageElement;

    return new Overlay({
      element: overlayEl,
      offset: [0, 30],
      positioning: 'top-center',
    });
  }

  #createWebGLObjectLayer(source: any) {
    return new WebGLVectorTileLayer({
      maxZoom: 23,
      properties: { name: 'objects' },
      source,
      style: this.objectStyle,
      visible: false,
      zIndex: 5,
    });
  }

  #createObjectLayer(source: VectorTileSource) {
    return new VectorTileLayer({
      minZoom: 23,
      properties: { name: 'objects' },
      source,
      style: this.objectStyle,
      visible: false,
      zIndex: 5,
    });
  }

  #createNdviWebGLLayer(source: Vector) {
    // [101, 67, 33], // Brown   (NDVI <= 0)
    // [255, 255, 0], // Yellow  (NDVI ~ 0.2)
    // [173, 255, 47], // Light Green (NDVI ~ 0.4)
    // [0, 255, 0], // Green   (NDVI ~ 0.6)
    // [0, 82, 0], // Dark Green (NDVI >= 1)

    return new WebGLVectorLayer({
      maxZoom: 25,
      properties: { name: 'ndvi' },
      source,
      style: {
        'circle-fill-color': [
          'interpolate',
          ['linear'],
          ['get', 'ndvi'],
          0,
          'rgba(101, 67, 33, 0.3)',
          0.2,
          'rgba(255, 255, 0, 0.3)',
          0.4,
          'rgba(173, 255, 47, 0.3)',
          0.6,
          'rgba(0, 255, 0, 0.3)',
          1,
          'rgba(0, 82, 0, 0.3)',
        ],
        'circle-radius': 1,
        'circle-stroke-color': [
          'interpolate',
          ['linear'],
          ['get', 'ndvi'],
          0,
          'rgba(101, 67, 33, 1)',
          0.2,
          'rgba(255, 255, 0, 1)',
          0.4,
          'rgba(173, 255, 47, 1)',
          0.6,
          'rgba(0, 255, 0, 1)',
          1,
          'rgba(0, 82, 0, 1)',
        ],
        'circle-stroke-width': 1,
      } as any,
      visible: false,
      zIndex: 5,
    });
  }

  #createNdviLayer(source: Vector) {
    return new VectorLayer({
      minZoom: 25,
      properties: { name: 'ndvi' },
      source,
      style: {
        'text-background-fill-color': 'white',
        'text-fill-color': 'black',
        'text-font': '11px sans-serif',
        'text-padding': [1, 1, 1, 4],
        'text-value': ['to-string', ['get', 'ndvi']],
      },
      visible: false,
      zIndex: 5,
    });
  }

  #createThermalWebGLLayer(source: Vector) {
    // [0, 0, 255], // Blue   (Temp <= 0)
    // [255, 0, 0], // Yellow  (Temp ~ 100)

    return new WebGLVectorLayer({
      maxZoom: 25,
      properties: { name: 'thermal' },
      source,
      style: {
        'circle-fill-color': [
          'interpolate',
          ['linear'],
          ['get', 'temperature'],
          10,
          'rgba(0, 0, 255, 0.3)',
          30,
          'rgba(255, 0, 0, 0.3)',
        ],
        'circle-radius': 1,
        'circle-stroke-color': [
          'interpolate',
          ['linear'],
          ['get', 'temperature'],
          10,
          'rgba(0, 0, 255, 1)',
          30,
          'rgba(255, 0, 0, 1)',
        ],
        'circle-stroke-width': 1,
      } as any,
      visible: false,
      zIndex: 5,
    });
  }

  #createThermalLayer(source: Vector) {
    return new VectorLayer({
      minZoom: 25,
      properties: { name: 'thermal' },
      source,
      style: {
        'text-background-fill-color': 'white',
        'text-fill-color': 'black',
        'text-font': '11px sans-serif',
        'text-padding': [1, 1, 1, 4],
        'text-value': ['concat', ['get', 'temperature'], '°C'],
      },
      visible: false,
      zIndex: 5,
    });
  }
}
